import { z } from 'zod';
import { StageId } from './awards.js';
import { Cents } from './common.js';
import { DocumentPurpose } from './documents.js';
import { ElectionChoiceId } from './queue.js';

/**
 * The award record (G1) beyond the award itself (APP-069): its stages in words, who entered and
 * approved it, each person's facts and value over time, and what this reader may do with its files.
 * Read with the record's own access (staff in scope, or the person who entered it).
 */

/** One stage card: its words come from the server so the record and its PDF say the same. */
export const RecordStage = z.object({
  id: StageId,
  status: z.enum(['done', 'current', 'upcoming', 'skipped']),
  /** "Approved by Graham Whitlock · Oct 15, 2026, 9:12 AM PDT", "Now · since Oct 13, 2026". */
  line: z.string(),
  /** What happened there, or what happens there: "Farah Haddad entered it and sent it." */
  story: z.string(),
});
export type RecordStage = z.infer<typeof RecordStage>;

/** Someone's step, as the trail recorded it: the roles they acted in then, in words. */
export const RecordStep = z.object({
  name: z.string(),
  onBehalfOf: z.string().nullable(),
  /** "Sector Lead · Maritime Airborne"; null for MyRewards' own steps. */
  roles: z.string().nullable(),
  at: z.string(),
});
export type RecordStep = z.infer<typeof RecordStep>;

/** One point in a person's value over time (the History drawer). */
export const ValuePoint = z.object({
  at: z.string(),
  /** PAA: the total value entered; null otherwise. */
  totalValueCents: Cents.nullable(),
  cashCents: Cents,
  options: z.number().int(),
  /** `previewedBy`: who was signed in when `name` was acted as in Preview as (APP-150); else null. */
  by: RecordStep.extend({ previewedBy: z.string().nullable().default(null) }),
  /** "Entered with the award", "Changed at approval". */
  what: z.string(),
  reason: z.string().nullable(),
  /** The audit source key (`screen`, `csv_import`, …); the screen puts it in words. */
  source: z.string(),
});
export type ValuePoint = z.infer<typeof ValuePoint>;

export const RecordPerson = z.object({
  personId: z.uuid(),
  /** Their line on the award: files and file rights name lines. */
  lineId: z.uuid(),
  employeeNumber: z.string().nullable(),
  /** Their sector as the award saw them ("Maritime HydroVision"). */
  sector: z.string().nullable(),
  costType: z.enum(['OH', 'GA']).nullable(),
  /**
   * The estimated tax (PAA cash), for the Stock Admin team and Payroll only; null for anyone else
   * and until it's entered. The chosen option's when they've chosen, else the range.
   */
  estimatedTax: z
    .object({ choice: ElectionChoiceId.nullable(), lowCents: Cents, highCents: Cents })
    .nullable(),
  /** They still have to choose cash, stock or a mix (Employee's choice). */
  choosing: z.boolean(),
  /** Their value over time, oldest first, from the trail this reader may see. */
  history: z.array(ValuePoint),
});
export type RecordPerson = z.infer<typeof RecordPerson>;

/** What this reader may do with the award's files now (the same rule the upload routes use). */
export const RecordFileRights = z.object({
  /** Each kind of file they may add: to the whole award, and/or to these people's lines. */
  canAdd: z.array(
    z.object({ purpose: DocumentPurpose, award: z.boolean(), lineIds: z.array(z.uuid()) }),
  ),
  /** For each current file: whether they may upload over it or delete it. */
  files: z.array(z.object({ id: z.uuid(), replace: z.boolean(), delete: z.boolean() })),
});
export type RecordFileRights = z.infer<typeof RecordFileRights>;

/** A Spot award's nomination, which its record names apart from its approval (QA-054). */
export const RecordNomination = z.object({
  /** Who wrote and sent it; null when this reader may not see who nominated. */
  name: z.string().nullable(),
  /** When it was sent. */
  at: z.string(),
});
export type RecordNomination = z.infer<typeof RecordNomination>;

/** A decision on the award's way, as the record lists it (APP-148). */
export const RecordDecision = z.object({
  at: z.string(),
  what: z.enum([
    'entered',
    'sent_to_executives',
    'approved',
    'declined',
    'rejected',
    'returned',
    'recorded',
  ]),
  by: RecordStep,
  /**
   * The role the person `by.onBehalfOf` names held for it, as recorded when a Stock Admin acted
   * for them ("Executive", "Sector Lead · Terrestrial"); null otherwise, such as a delegate's entry
   * for their Sector Lead. Optional: older servers omit it.
   */
  forRole: z.string().nullable().optional(),
  /**
   * What else the decision says: why it went to the Executives ("Terrestrial over budget by
   * $2,913"), the top-up an approval added, the reason for a decline, return or rejection, or who
   * approved a recorded decision. Amounts only to readers who may see that pool's budget.
   */
  detail: z.string().nullable(),
});
export type RecordDecision = z.infer<typeof RecordDecision>;

export const AwardRecordFacts = z.object({
  stages: z.array(RecordStage),
  /** Who entered it. A Spot award is made by its approval, so this is its approver. */
  enteredBy: RecordStep,
  /** A Spot award's nomination; null for every other award. */
  nominatedBy: RecordNomination.nullable(),
  /** Null until it's approved, or when it never needed approval. */
  approvedBy: RecordStep.nullable(),
  people: z.array(RecordPerson),
  files: RecordFileRights,
  /** Each decision, oldest first, with who made it and for whom. */
  decisions: z.array(RecordDecision),
});
export type AwardRecordFacts = z.infer<typeof AwardRecordFacts>;
